import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import CustomerLayout from "@/components/CustomerLayout";
import StoreCard from "@/components/StoreCard";
import { SkeletonCard, EmptyState } from "@/components/States";
import { Store } from "lucide-react";
import { useSeo } from "@/lib/seo";

const FILTERS = [
  { label: "All", value: "", testid: "store-filter-all" },
  { label: "Grocery", value: "grocery", testid: "store-filter-grocery" },
  { label: "Dairy", value: "dairy", testid: "store-filter-dairy" },
  { label: "Fruits & Veg", value: "fruits-vegetables", testid: "store-filter-fruits" },
  { label: "Bakery", value: "bakery", testid: "store-filter-bakery" },
];

export default function Stores() {
  const { location } = useLocation();
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("distance");
  useSeo({ title: "Stores near you", description: "Discover trusted local grocery and kirana stores near you on Grofkart.", path: "/stores" });
  const { data: stores, isLoading } = useQuery({
    queryKey: ["stores-all", cat, sort, location.lat],
    queryFn: () => api.get("/stores", { params: { lat: location.lat, lng: location.lng, category: cat || undefined, sort } }).then((r) => r.data),
  });

  return (
    <CustomerLayout>
      <h1 className="mb-1 font-head text-2xl font-extrabold text-slate-900 sm:text-3xl">Stores near you</h1>
      <p className="mb-4 text-sm text-slate-500">Delivering to {location.address}</p>

      <div className="mb-5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
        {FILTERS.map((f) => (
          <button key={f.value} data-testid={f.testid} onClick={() => setCat(f.value)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === f.value ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-brand/40"}`}>
            {f.label}
          </button>
        ))}
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto shrink-0 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 outline-none">
          <option value="distance">Nearest</option>
          <option value="rating">Top rated</option>
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : stores?.length ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {stores.map((s) => <StoreCard key={s.id} store={s} />)}
        </div>
      ) : (
        <EmptyState icon={Store} title="No stores found" subtitle="Try a different category or location." />
      )}
    </CustomerLayout>
  );
}
